import FourOhFour from 'routes/status/FourOhFour'
import { useAppSelector } from 'store'
import { DataGridPro, GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid-pro'
import { Badge, Box, Button, Paper, Snackbar } from '@material-ui/core'
import useGetHighschoolsExplore from './useGetHighschoolsExplore'
import getColumns from './columns'
import { useTableHeightListener } from './useTableHeightListener'
import { useEffect, useMemo, useReducer, useRef } from 'react'
import highSchoolExploreReducer from './state'
import { PAGE_SIZE_OPTIONS, PageSizeOption, SortKey, stringAsValidSortKey } from './types'
import ExportButton from './ExportButton'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter } from '@fortawesome/pro-solid-svg-icons'
import HighschoolPreviewDrawer from '../Preview/HighschoolPreviewDrawer'
import FilterDrawer from '../components/FilterDrawer'
import getFilterCount from '../filter-state-count'
import useSyncStateFromURL from './useSyncStateFromUrl'
import { decodeExploreStateParams, ExploreTableState } from './filter-params'
import { createAPIFiltersFromUIFilters } from '../filter-state-to-api-query'
import { hasPermission } from 'store/user'
import { faMapLocationDot } from '@fortawesome/pro-regular-svg-icons'
import { Link } from 'react-router-dom'
import { encodeExploreMapState } from '../filter-state-to-map-url-params'
import SearchTextField from './SearchTextField'
import ExploreToolbar from './ExploreToolbar'

export default function ExploreTable({ testOnlyDisableVirtualization }: { testOnlyDisableVirtualization?: boolean }) {
  const canView = useAppSelector((state) =>
    hasPermission(state, 'PERFORM_VISIT_MANAGEMENT_TASKS_FOR_ASSOCIATED_COLLEGE'),
  )
  const [tableHeight, ref] = useTableHeightListener()
  const [urlState, syncStateToUrl] = useSyncStateFromURL()
  const exploreState = decodeExploreStateParams(urlState)
  const { pageSize, sort, sortDir, currentPage, ...filters } = exploreState
  const [state, dispatch] = useReducer(highSchoolExploreReducer, {
    currentPage: currentPage ? currentPage : 0,
    selectedSchools: [],
    pageSize: pageSize ? pageSize : PAGE_SIZE_OPTIONS[0],
    sortModel: sort && sortDir ? [{ field: sort, sort: sortDir }] : [{ field: 'name', sort: 'asc' }],
    filters: filters,
    isFilterDrawerOpen: false,
  })

  useEffect(() => {
    const { sort: sortDir, field: sortKey } = state.sortModel[0] || {}
    const key = stringAsValidSortKey(sortKey)
    const newUrlState: ExploreTableState = {
      ...state.filters,
      pageSize: state.pageSize,
      currentPage: state.currentPage,
      ...(sortDir && { sortDir }),
      ...(key && { sort: key }),
    }
    syncStateToUrl(newUrlState)
  }, [state.filters, state.pageSize, state.sortModel, state.currentPage, syncStateToUrl])

  const isPremiumExplorerEnabled = useAppSelector(
    (state) => state.college.record?.Features?.SupportsPremiumHighSchoolExplorer || false,
  )

  const columns = getColumns({
    onClickPreview: (id) => {
      dispatch({ type: 'preview-highschool-change', previewHighschoolId: id })
    },
    showPremiumColumns: isPremiumExplorerEnabled,
  })

  const modelSortKey = columnFieldToSortKey(state.sortModel[0]?.field)
  const getHighschoolExplore = useGetHighschoolsExplore({
    resultOptions: {
      from: state.currentPage * state.pageSize,
      limit: state.pageSize,
      sortKey: modelSortKey ? modelSortKey : sort || 'name',
      sortDirection: state.sortModel[0]?.sort ? state.sortModel[0]?.sort : sortDir || 'asc',
    },
    filters: createAPIFiltersFromUIFilters(state.filters),
    enabled: canView,
  })

  const highschools = useMemo(() => getHighschoolExplore?.data?.highSchools || [], [getHighschoolExplore])
  const totalPages = !getHighschoolExplore?.data?.total ? 1 : getHighschoolExplore?.data?.total / state.pageSize

  const previewHighschoolIndex = state.previewHighschoolId
    ? highschools.findIndex((r) => r.id === state.previewHighschoolId)
    : 0

  // Restores previewHighschoolId when next/previous buttons cross a pagination boundary. Stored in ref so it can update without a re-render which is important when used in `useEffect`.
  const requestedPreviewHighschoolIndex = useRef<number | undefined | null>()

  const previewHighSchool = highschools?.[previewHighschoolIndex]

  const hasNextPreview = previewHighschoolIndex < highschools.length - 1 || state.currentPage < totalPages - 1

  const hasPrevPreview = previewHighschoolIndex > 0 || state.currentPage > 0

  const handleClickNextRecord = (highschoolId?: string) => {
    if (highschools.length === 0 || highschoolId === undefined) {
      return
    }

    const nextIndex = previewHighschoolIndex + 1
    if (nextIndex === highschools.length && state.currentPage < totalPages - 1) {
      dispatch({
        type: 'page-change',
        newPage: state.currentPage + 1,
      })
      requestedPreviewHighschoolIndex.current = 0
      return
    }
    dispatch({
      type: 'preview-highschool-change',
      previewHighschoolId: highschools[nextIndex].id,
    })
  }

  const handleClickPreviousRecord = (highschoolId?: string) => {
    if (highschools.length === 0 || highschoolId === undefined) {
      return
    }

    const previousIndex = previewHighschoolIndex - 1
    if (previousIndex < 0 && state.currentPage > 0) {
      dispatch({
        type: 'page-change',
        newPage: state.currentPage - 1,
      })
      requestedPreviewHighschoolIndex.current = state.pageSize - 1
      return
    }
    dispatch({
      type: 'preview-highschool-change',
      previewHighschoolId: highschools[previousIndex].id,
    })
  }

  useEffect(() => {
    if (!getHighschoolExplore.isLoading && requestedPreviewHighschoolIndex?.current != undefined) {
      dispatch({
        type: 'preview-highschool-change',
        previewHighschoolId: highschools[requestedPreviewHighschoolIndex.current].id,
      })
    }
  }, [getHighschoolExplore.isLoading, highschools])

  const filterCount = getFilterCount(state.filters)

  if (!canView) {
    return <FourOhFour />
  }

  return (
    <Box>
      <Paper elevation={0}>
        <Snackbar
          open={getHighschoolExplore.isError}
          message="There was an error getting high schools."
        />
        <Box
          display="flex"
          flexWrap="nowrap"
          alignItems="center"
          justifyContent="space-between"
          p={2}
        >
          <Box
            display="flex"
            gridGap={8}
          >
            <SearchTextField
              defaultValue={state.filters.text}
              onChange={(val) => {
                dispatch({ type: 'filter-change', filters: { ...state.filters, text: val || undefined } })
              }}
            />
            <ExploreToolbar
              selectedSchools={state.selectedSchools}
              schoolData={getHighschoolExplore?.data?.highSchools}
            />
          </Box>
          <Box
            display="flex"
            gridGap={8}
          >
            <Badge
              color="primary"
              badgeContent={filterCount}
              aria-label="count of applied filters"
            >
              <Button
                color="primary"
                onClick={() => dispatch({ type: 'filter-drawer-toggled', isOpen: true })}
                startIcon={<FontAwesomeIcon icon={faFilter} />}
              >
                Filters
              </Button>
            </Badge>
            <FilterDrawer
              defaultFilters={state.filters}
              isOpen={state.isFilterDrawerOpen}
              onClose={() => {
                dispatch({ type: 'filter-drawer-toggled', isOpen: false })
              }}
              onClickApply={(f) => {
                dispatch({
                  type: 'filter-change',
                  filters: f,
                })
              }}
              showPremiumFilters={isPremiumExplorerEnabled}
            />
            {isPremiumExplorerEnabled && (
              <ExportButton
                columns={columns}
                resultOptions={{
                  from: 0,
                  limit: getHighschoolExplore.data?.maxExportLimit || 0,
                  sortKey: columnFieldToSortKey(state.sortModel[0]?.field) || 'name',
                  sortDirection: state.sortModel[0]?.sort || 'asc',
                }}
                filters={state.filters}
              />
            )}
            <Button
              component={Link}
              to={`./highschools/map?${encodeExploreMapState({
                ...state.filters,
                lat: getHighschoolExplore.data?.center?.lat,
                lng: getHighschoolExplore.data?.center?.lng,
                zoom: 6,
              }).toString()}`}
              variant="outlined"
              color="primary"
              disabled={
                getHighschoolExplore.isLoading ||
                getHighschoolExplore.data?.total === 0 ||
                !getHighschoolExplore.data?.center
              }
              startIcon={<FontAwesomeIcon icon={faMapLocationDot} />}
            >
              View in Map
            </Button>
          </Box>
        </Box>
        <DataGridPro
          ref={ref}
          sx={{
            '&.MuiDataGrid-root': {
              height: `calc(100vh - ${tableHeight}px)`,
            },
          }}
          disableColumnReorder={true}
          columns={columns}
          disableVirtualization={testOnlyDisableVirtualization}
          rows={getHighschoolExplore.data?.highSchools || []}
          sortingOrder={['desc', 'asc']}
          rowCount={getHighschoolExplore.data?.total || 0}
          loading={getHighschoolExplore.isLoading}
          disableColumnFilter
          disableColumnMenu
          disableSelectionOnClick
          pagination
          paginationMode="server"
          rowsPerPageOptions={[...PAGE_SIZE_OPTIONS]}
          page={state.currentPage}
          onPageChange={(page) => {
            dispatch({ type: 'page-change', newPage: page })
          }}
          pinnedColumns={{ left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'name'] }}
          pageSize={state.pageSize}
          onPageSizeChange={(size) => {
            dispatch({ type: 'pageSize-change', pageSize: size as PageSizeOption })
          }}
          sortingMode="server"
          checkboxSelection
          selectionModel={state.selectedSchools}
          onSelectionModelChange={(newSelection) => {
            dispatch({ type: 'selection-change', selectedSchools: newSelection.map(String) })
          }}
          sortModel={state.sortModel}
          onSortModelChange={(sort) => {
            dispatch({ type: 'sortModel-change', sortModel: sort })
          }}
        />
      </Paper>
      <HighschoolPreviewDrawer
        open={!!state.previewHighschoolId}
        dataIsLoading={getHighschoolExplore.isLoading}
        onClickNext={handleClickNextRecord}
        onClickPrev={handleClickPreviousRecord}
        hasNext={hasNextPreview}
        hasPrevious={hasPrevPreview}
        highschool={previewHighSchool}
        onClose={() => {
          dispatch({ type: 'preview-highschool-change', previewHighschoolId: null })
          requestedPreviewHighschoolIndex.current = null
        }}
      />
    </Box>
  )
}

function columnFieldToSortKey(field?: string): SortKey | null {
  switch (field) {
    case 'name':
      return 'name'
    case 'state':
      return 'state'
    case 'city':
      return 'city'
    case 'schoolType':
      return 'schoolType'
    case 'seniorClassSize':
      return 'seniorClassSize'
    case 'nextVisitDate':
      return 'nextVisitDate'
    case 'title1Program':
      return 'title1Program'
    case 'pctFreeReducedLunch':
      return 'pctFreeReducedLunch'
    case 'isActive':
      return 'isActive'
    default:
      return null
  }
}
